import { Navigate, Outlet, Route, useLocation } from 'react-router'
import { BrowserRouter } from 'react-router-dom'
import { PageLayout } from '../../components/PageLayout'
import { AnalyticsProvider } from '../../context/useAnalytics'
import { UserProvider } from '../../context/useCurrentUser'
import { FeatureFlagProvider } from '../../context/useFeatureFlag'
import { SentryProvider } from '../../context/useSentry'
import { useRedirect } from '../../utils/useRedirect'
import { WalletConnectDialogProvider } from '../../web3/connectDialog/WalletConnectDialogProvider'
import { ConnectedWalletProvider } from '../../web3/ConnectedWalletProvider'
import { Web3Provider } from '../../web3/Web3Provider'
import { BillsPage } from '../bills/BillsPage'
import { FindMyBillsPage } from '../findMyBills/FindMyBillsPage'
import { OffRampPage } from '../offramp/OffRampPage'
import FeedbackPage from '../public/FeedbackPage'
import ReferralsProgramPage from '../referralsProgram/ReferralsProgramPage'
import ForgotPasswordPage from '../signin/ForgotPasswordPage/ForgotPasswordPage'
import { NewPasswordPage } from '../signin/NewPasswordPage/NewPasswordPage'
import PublicLayout from '../signin/PublicLayout/PublicLayout'
import SignInPage from '../signin/SignIn/SignInPage'
import SignUpPage from '../signin/SignUp/SignUpPage'
import TermsAndConditionsPage from '../signin/SignUp/termsAndConditions/TermsAndConditionsPage'
import { useAuthContext } from './auth'
import { Redirect } from './Redirect'
import { SentryRoutes } from './sentry'
import { IntercomProvider } from '../../context/useIntercom'
import { Profile } from '../profile/Profile'
import { VerificationPage } from '../verification/VerificationPage'
import { TooltipProvider } from '@spritz-finance/ui'
import { SpritzCardPage } from '../spritzCard/SpritzCardPage'
import { FeedbackProvider } from '../../context/useFeedback'

const CatchAll = () => {
    const { user, previousSignin } = useAuthContext()
    const location = useLocation()

    if (!user && location.search === 'callback') {
        return <div />
    }

    if (user) {
        return <Navigate to={'bills'} replace />
    }

    return <Navigate to={previousSignin ? '/signin' : '/signup'} />
}

const RequireAuth = () => {
    const { user, previousSignin } = useAuthContext()
    const { storeLastLocation } = useRedirect()

    if (!user) {
        storeLastLocation()
        return <Navigate to={previousSignin ? '/signin' : '/signup'} />
    }

    return (
        <TooltipProvider>
            <UserProvider>
                <IntercomProvider>
                    <FeatureFlagProvider>
                        <ConnectedWalletProvider>
                            <Web3Provider>
                                <WalletConnectDialogProvider>
                                    <Redirect>
                                        <Outlet />
                                    </Redirect>
                                </WalletConnectDialogProvider>
                            </Web3Provider>
                        </ConnectedWalletProvider>
                    </FeatureFlagProvider>
                </IntercomProvider>
            </UserProvider>
        </TooltipProvider>
    )
}

export const AppRoutes = () => {
    return (
        <BrowserRouter>
            <AnalyticsProvider>
                <SentryProvider>
                    <FeedbackProvider>
                        <SentryRoutes>
                            <Route index element={<CatchAll />} />
                            <Route element={<PublicLayout />}>
                                <Route path="new-password" element={<NewPasswordPage />} />
                                <Route path="signin" element={<SignInPage />} />
                                <Route path="signup" element={<SignUpPage />} />
                                <Route path="feedback" element={<FeedbackPage />} />
                                <Route path="forgot-password" element={<ForgotPasswordPage />} />
                            </Route>
                            <Route
                                path="terms-and-conditions"
                                element={<TermsAndConditionsPage />}
                            />
                            <Route path="/" element={<RequireAuth />}>
                                <Route element={<PageLayout />}>
                                    <Route path="bills/*" element={<BillsPage />} />
                                    <Route path="offramp/*" element={<OffRampPage />} />
                                    <Route path="profile" element={<Profile />} />
                                    <Route path="referrals/*" element={<ReferralsProgramPage />} />
                                    <Route path="find-my-bills" element={<FindMyBillsPage />} />
                                    <Route path="verification" element={<VerificationPage />} />
                                    <Route path="card/*" element={<SpritzCardPage />} />
                                </Route>
                            </Route>
                            <Route path="*" element={<CatchAll />} />
                        </SentryRoutes>
                    </FeedbackProvider>
                </SentryProvider>
            </AnalyticsProvider>
        </BrowserRouter>
    )
}
